import { create } from "zustand";

import {
  addExtraExercise,
  ApiError,
  discardSession,
  finishSession,
  getSession,
  newClientId,
  startSession,
  swapExercise,
  type Exercise,
  type LoggedSet,
  type PersonalRecord,
  type PlanItem,
  type SessionDetail,
  type SetKind,
} from "../lib/api";
import {
  applySwaps,
  cacheSession,
  cachedSession,
  dropSession,
  enqueue,
  flush,
  pendingCount,
  rememberSwap,
  swapsFor,
} from "../lib/offline";

/**
 * Идущая тренировка.
 *
 * Единственный путь, которым экран сессии общается с миром. Порядок в каждой
 * мутации один и тот же: сначала экран, потом IndexedDB, потом очередь, и
 * только потом сеть. В зале ждать ответа сервера между подходами нельзя, а
 * связь там рвётся регулярно.
 *
 * Идентификатор идущей тренировки лежит в localStorage: уход из приложения,
 * звонок или разряд не должны стоить записанных подходов (§5 ТЗ).
 */

const ACTIVE_KEY = "rack.active-session";

type Status = "idle" | "loading" | "ready" | "error";

export interface SetDraft {
  exercise_id: number;
  program_exercise_id: number | null;
  order_index: number;
  weight_kg: number | null;
  reps: number | null;
  /**
   * Обязательно, а не опционально: сервер при правке перезаписывает все поля
   * подхода, и правка веса без RPE молча стёрла бы его.
   */
  rpe: number | null;
  kind: SetKind;
}

interface SessionState {
  active: SessionDetail | null;
  status: Status;
  error: string | null;
  /** Сколько мутаций ждут отправки. Ноль — всё дошло. */
  pending: number;
  /** Что сейчас начинается: день программы или "free". Не null — второй старт игнорируется. */
  starting: number | "free" | null;
  /** Почему не начался последний старт — показывается там же, где нажали. */
  startError: { day: number | "free"; message: string } | null;

  restore: () => Promise<void>;
  start: (programDayId: number | null) => Promise<SessionDetail | null>;
  saveSet: (setId: string | null, draft: SetDraft) => Promise<void>;
  removeSet: (setId: string) => Promise<void>;
  swap: (item: PlanItem, exercise: Exercise) => Promise<void>;
  addExtra: (exercise: Exercise) => Promise<void>;
  finish: () => Promise<PersonalRecord[]>;
  /** Выйти без сохранения — тренировка, в которой ничего не записано. */
  discard: () => Promise<void>;
  sync: () => Promise<void>;
}

const remember = (id: string) => localStorage.setItem(ACTIVE_KEY, id);
const forget = () => localStorage.removeItem(ACTIVE_KEY);
const message = (e: unknown) =>
  e instanceof Error ? e.message : String(e);

/** Почему не начали — словами человека, а не «POST /sessions → 500». */
function startFailure(e: unknown): string {
  if (e instanceof ApiError) {
    return e.status >= 500
      ? "Сервер не смог собрать тренировку. Попробуйте ещё раз."
      : e.message;
  }
  return "Нет связи с сервером. Проверьте интернет и нажмите ещё раз.";
}

export const useSession = create<SessionState>((set, get) => ({
  active: null,
  status: "idle",
  error: null,
  pending: 0,
  starting: null,
  startError: null,

  /** Отправить накопленное и показать, сколько ещё в очереди. */
  async sync() {
    const result = await flush();
    set({ pending: result.pending });
    // Перечитываем тренировку с сервера только когда очередь пуста. Иначе
    // ответ сервера затрёт подход, который ещё не успел уйти, и он моргнёт
    // на экране — а в зале это выглядит как потерянная работа.
    if (result.sent > 0 && result.pending === 0 && get().active) {
      // План мог измениться: например, восстановилась замена упражнения.
      await get().restore();
    }
  },

  /**
   * Поднять тренировку, начатую до перезагрузки.
   *
   * Сперва из IndexedDB — она отвечает мгновенно и работает без сети. Сервер
   * спрашиваем следом и только чтобы уточнить; его молчание не повод показать
   * пустой экран поверх записанных подходов.
   */
  async restore() {
    const id = localStorage.getItem(ACTIVE_KEY);
    if (!id) return;

    const local = await cachedSession(id);
    if (local) set({ active: local, status: "ready" });
    else set({ status: "loading" });

    set({ pending: await pendingCount() });

    try {
      await flush();
      const fresh = await getSession(id);
      if (fresh.finished_at) {
        await dropSession(id);
        forget();
        set({ active: null, status: "idle", pending: await pendingCount() });
        return;
      }
      // Замены до первого записанного подхода сервер восстановить не может —
      // держим их локально и подмешиваем поверх свежего плана.
      const merged = { ...fresh, plan: applySwaps(fresh.plan, await swapsFor(id)) };
      await cacheSession(merged);
      set({ active: merged, status: "ready", error: null, pending: await pendingCount() });
    } catch (e) {
      if (local) {
        // Работаем на локальной копии — это штатный режим, а не ошибка.
        set({ status: "ready", pending: await pendingCount() });
        return;
      }
      forget();
      set({ active: null, status: "idle", error: message(e) });
    }
  },

  async start(programDayId) {
    // Второй тап, пока первый старт в пути, создал бы вторую тренировку — так
    // в базе и появлялись пары пустых тренировок с разницей в секунды.
    if (get().starting !== null) return null;
    const day = programDayId ?? "free";
    set({ status: "loading", error: null, starting: day, startError: null });
    try {
      const session = await startSession(newClientId(), programDayId);
      remember(session.client_id);
      await cacheSession(session);
      set({ active: session, status: "ready", pending: await pendingCount(), starting: null });
      return session;
    } catch (e) {
      // Начать тренировку без сети нельзя: сервер выдаёт план и рекомендации.
      set({
        status: "error",
        error: message(e),
        starting: null,
        startError: { day, message: startFailure(e) },
      });
      return null;
    }
  },

  /** Записать или поправить подход. */
  async saveSet(setId, draft) {
    const session = get().active;
    if (!session) return;

    const clientId = setId ?? newClientId();
    const optimistic: LoggedSet = {
      client_id: clientId,
      ...draft,
      rir: null,
      completed_at: new Date().toISOString(),
    };
    const next = {
      ...session,
      sets: [...session.sets.filter((s) => s.client_id !== clientId), optimistic],
    };
    set({ active: next, error: null });
    await cacheSession(next);
    await enqueue(session.client_id, "set", { client_id: clientId, ...draft });
    await get().sync();
  },

  async removeSet(setId) {
    const session = get().active;
    if (!session) return;
    const next = {
      ...session,
      sets: session.sets.filter((s) => s.client_id !== setId),
    };
    set({ active: next });
    await cacheSession(next);
    await enqueue(session.client_id, "delete-set", { client_id: setId });
    await get().sync();
  },

  /**
   * Заменить упражнение на лету: «тренажёр занят».
   *
   * Цель остаётся от программы. Без сети рекомендацию по новому упражнению
   * взять неоткуда — тогда честно говорим об этом, а не показываем вес от
   * исходного движения: он заведомо неверный.
   */
  async swap(item, exercise) {
    const session = get().active;
    if (!session) return;

    const put = (replacement: PlanItem) => {
      const next = {
        ...session,
        plan: session.plan.map((p) =>
          p.program_exercise_id === replacement.program_exercise_id ? replacement : p,
        ),
      };
      set({ active: next });
      return Promise.all([cacheSession(next), rememberSwap(session.client_id, replacement)]);
    };

    if (item.program_exercise_id === null) return; // добавленное менять незачем
    try {
      await put(await swapExercise(session.client_id, item.program_exercise_id, exercise.id));
      set({ error: null });
    } catch (e) {
      // Сервер отказал по существу — например, подходы уже записаны. Подменять
      // упражнение локально в обход этого отказа нельзя: получится расхождение
      // с тем, что лежит в базе.
      if (e instanceof ApiError) {
        set({ error: message(e) });
        return;
      }
      await put({
        ...item,
        exercise,
        is_swapped: true,
        previous: [],
        // Без сети рекорда по этому упражнению не узнать.
        record_weight_kg: null,
        recommendation: {
          weight_kg: null,
          reps_hint: item.recommendation.reps_hint,
          why: "нет связи — историю по этому упражнению не поднять, вес выбери сам",
          basis: "none",
        },
      });
    }
  },

  /**
   * Добавить упражнение прямо во время тренировки.
   *
   * Программу это не меняет: человек решил сделать движение сегодня. Без сети
   * добавляем всё равно — записать подходы важнее рекомендации, а прошлый раз
   * подтянется, когда очередь дойдёт до сервера.
   */
  async addExtra(exercise) {
    const session = get().active;
    if (!session) return;
    // Уже есть в тренировке — второй блок завёл бы подходы, которые сервер
    // после перезагрузки не покажет ни здесь, ни в программном блоке.
    if (session.plan.some((p) => p.exercise.id === exercise.id)) return;

    const put = (item: PlanItem) => {
      const current = get().active;
      if (!current) return Promise.resolve();
      const next = { ...current, plan: [...current.plan, item] };
      set({ active: next });
      return Promise.all([cacheSession(next), rememberSwap(current.client_id, item)]);
    };

    try {
      await put(await addExtraExercise(session.client_id, exercise.id));
      set({ error: null });
    } catch {
      await put({
        program_exercise_id: null,
        order_index: 1000 + exercise.id,
        exercise,
        target_sets: null,
        target_reps_min: null,
        target_reps_max: null,
        rep_sequence: null,
        target_spec: { type: "free" },
        rest_sec: null,
        notes: null,
        superset_group: null,
        is_backoff: false,
        is_swapped: false,
        alternatives: [],
        previous: [],
        // Без сети рекорда по этому упражнению не узнать.
        record_weight_kg: null,
        recommendation: {
          weight_kg: null,
          reps_hint: "",
          why: "нет связи — прошлый раз подтянется позже, вес выбери сам",
          basis: "none",
        },
      });
    }
  },

  /**
   * Завершить тренировку.
   *
   * Локально она завершается сразу, даже без сети: человек ушёл из зала, и
   * держать экран открытым до восстановления связи незачем. Завершение уходит
   * той же очередью, что и подходы, и в том же порядке — после них.
   */
  async finish() {
    const session = get().active;
    if (!session) return [];

    // Сначала досылаем подходы: завершение не должно их обогнать, иначе сервер
    // посчитает рекорды по неполной тренировке.
    const { pending } = await flush();

    let records: PersonalRecord[] = [];
    if (pending === 0) {
      try {
        // Пустую тренировку сервер удаляет и отвечает без тела — рекордов нет.
        records = (await finishSession(session.client_id))?.new_records ?? [];
      } catch {
        // Связь пропала между досылкой и завершением — уходим в очередь.
        await enqueue(session.client_id, "finish", {});
      }
    } else {
      // Что-то ещё не дошло: завершение встаёт в ту же очередь, за подходами.
      // Рекорды тогда посчитаются на сервере молча — праздновать нечем, но и
      // выдумывать их на клиенте нельзя.
      await enqueue(session.client_id, "finish", {});
    }

    await dropSession(session.client_id);
    forget();
    set({ active: null, status: "idle", error: null, pending: await pendingCount() });
    return records;
  },

  async discard() {
    const session = get().active;
    if (!session) return;
    // Досылать нечего: подходов нет. Но удаление подхода могло застрять в
    // очереди — отправляем его первым, чтобы сервер увидел пустую тренировку.
    await flush();
    try {
      await discardSession(session.client_id);
    } catch (e) {
      // Без сети — в очередь: локально тренировки уже нет, сервер узнает позже.
      if (!(e instanceof ApiError)) await enqueue(session.client_id, "discard", {});
    }
    await dropSession(session.client_id);
    forget();
    set({ active: null, status: "idle", error: null, pending: await pendingCount() });
  },
}));
